import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Box, 
  useToast, 
  Tabs, 
  TabList, 
  TabPanels, 
  Tab, 
  TabPanel,
  Alert,
  AlertIcon,
  AlertDescription,
  HStack,
  Text,
  Badge,
} from "@chakra-ui/react";
import { MdWifiOff, MdWifi } from "react-icons/md";
import TableBasedOrdersView from "./components/TableBasedOrdersView";
import DoneOrdersTable from "./components/DoneOrdersTable";
import { orderService } from '../../../services/orderService';
import io from 'socket.io-client';
import offlineStorage from '../../../services/offlineStorage';

export default function Orders() {
  const [activeOrders, setActiveOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [completedTimeRange, setCompletedTimeRange] = useState('today');
  const toast = useToast();
  
  // Siparişleri getir
  const fetchOrders = useCallback(async (timeRange = 'today') => {
    try {
      setLoading(true);
      const activeResponse = await orderService.getActiveOrders();
      const completedResponse = await orderService.getCompletedOrders(timeRange);
      
      const activeOrdersData = activeResponse.data || [];
      const completedOrdersData = completedResponse.data || [];
      
      // State'e set et
      setActiveOrders(activeOrdersData);
      setCompletedOrders(completedOrdersData);
      
      // Online ise ve API'den geldiyse cache'e kaydet (orderService zaten yapıyor ama emin olmak için)
      if (navigator.onLine && !activeResponse.fromCache && !completedResponse.fromCache) {
        // orderService zaten cache'e kaydediyor ama emin olmak için tekrar kaydet
        await offlineStorage.saveActiveOrders(activeOrdersData);
        await offlineStorage.saveCompletedOrders(completedOrdersData);
        await offlineStorage.setLastUpdateTime();
      }
      
      // Cache'den mi geldi kontrol et
      const fromCache = activeResponse.fromCache || completedResponse.fromCache || false;
      setIsFromCache(fromCache);
      
      // Son güncelleme zamanını al
      const lastUpdate = offlineStorage.getLastUpdateTime();
      setLastUpdateTime(lastUpdate);
      
      // Offline durumda ve cache'den geldiyse bilgilendir
      if (!isOnline && (activeResponse.fromCache || completedResponse.fromCache)) {
        toast({
          title: "Çevrimdışı Mod",
          description: "Siparişler cache'den gösteriliyor. Son güncelleme: " + 
            (lastUpdate ? new Date(lastUpdate).toLocaleString('tr-TR') : 'Bilinmiyor'),
          status: "info",
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Fetch orders error:', error);
      toast({
        title: "Hata",
        description: "Siparişler yüklenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  }, [completedTimeRange]); // completedTimeRange değiştiğinde yeniden çalıştır

  // Online/offline durumunu dinle
  useEffect(() => {
    let aggressiveCheckInterval = null;

    const handleOnline = async () => {
      setIsOnline(true);
      toast({
        title: "Bağlantı Kuruldu",
        description: "İnternet bağlantısı yeniden kuruldu. Siparişler güncelleniyor...",
        status: "success",
        duration: 3000,
        isClosable: true
      });
      
      // Bağlantı kurulunca hemen siparişleri çek
      await fetchOrders(completedTimeRange);
      
      // Offline sırasında kaçan siparişleri yakalamak için agresif kontrol
      // İlk 30 saniye her 10 saniyede bir kontrol et (daha az agresif)
      let checkCount = 0;
      const maxChecks = 3; // 30 saniye / 10 saniye = 3 kontrol
      
      aggressiveCheckInterval = setInterval(async () => {
        if (navigator.onLine) {
          checkCount++;
          await fetchOrders(completedTimeRange);
          
          // 30 saniye sonra (3 kontrol) durdur
          if (checkCount >= maxChecks) {
            clearInterval(aggressiveCheckInterval);
            aggressiveCheckInterval = null;
          }
        } else {
          clearInterval(aggressiveCheckInterval);
          aggressiveCheckInterval = null;
        }
      }, 10000); // 10 saniye (daha az agresif)
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: "Çevrimdışı Mod",
        description: "İnternet bağlantısı kesildi. Siparişler cache'den gösteriliyor. Bağlantı kurulunca otomatik güncellenecek.",
        status: "warning",
        duration: 5000,
        isClosable: true
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (aggressiveCheckInterval) {
        clearInterval(aggressiveCheckInterval);
      }
    };
  }, [toast, fetchOrders]);

  // Socket.io bağlantısı (sadece online iken)
  useEffect(() => {
    fetchOrders(completedTimeRange);
    
    // Online ise interval ve socket bağlantısı kur
    let interval;
    let socket;

    if (isOnline) {
      // Normal interval: 60 saniye (daha az sıklıkta refresh)
      interval = setInterval(() => {
        fetchOrders(completedTimeRange);
      }, 60000); // 60 saniye (30'dan 60'a çıkarıldı)

      socket = io(import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001', {
        transports: ['websocket']
      });

      socket.on('connect', () => {
        const restaurantId = localStorage.getItem('restaurantId');
        if (restaurantId) {
          socket.emit('join_restaurant', restaurantId);
        }
      });

      // Yeni sipariş geldiğinde (newOrderReceived event'i)
      socket.on('newOrderReceived', async (data) => {
        const order = data.order || data;
        
        // Siparişi listeye ekle (eğer zaten yoksa)
        setActiveOrders(prev => {
          const exists = prev.some(o => o._id === order._id || o.id === order._id);
          if (exists) return prev;
          const newOrders = [order, ...prev];
          
          // Cache'e tüm aktif siparişleri kaydet (güncel liste)
          offlineStorage.saveActiveOrders(newOrders).catch(err => 
            console.error('Cache kaydetme hatası:', err)
          );
          
          return newOrders;
        });
        
        // Cache'e tek sipariş olarak da kaydet
        await offlineStorage.saveOrder(order);
        
        // Son güncelleme zamanını güncelle
        await offlineStorage.setLastUpdateTime();
        
        // Toast bildirimi kaldırıldı - sadece NavbarNotificationAdmin'de gösterilecek
      });

      // Geriye dönük uyumluluk için new_order event'i (eski sistem)
      socket.on('new_order', async (order) => {
        // Siparişi listeye ekle
        setActiveOrders(prev => {
          const exists = prev.some(o => o._id === order._id || o.id === order._id);
          if (exists) return prev;
          const newOrders = [order, ...prev];
          
          // Cache'e tüm aktif siparişleri kaydet (güncel liste)
          offlineStorage.saveActiveOrders(newOrders).catch(err => 
            console.error('Cache kaydetme hatası:', err)
          );
          
          return newOrders;
        });
        
        // Cache'e tek sipariş olarak da kaydet
        await offlineStorage.saveOrder(order);
        
        // Son güncelleme zamanını güncelle
        await offlineStorage.setLastUpdateTime();
        
        // Bildirim göster
        toast({
          title: "Yeni Sipariş",
          description: `Masa ${order.masa_id || order.tableId || 'Bilinmeyen'}'den yeni sipariş geldi`,
          status: "info",
          duration: 5000,
          isClosable: true
        });
      });
    }

    return () => {
      if (interval) clearInterval(interval);
      if (socket) socket.disconnect();
    };
  }, [isOnline, toast]);

  // Sipariş durumu güncelleme
  const handleStatusChange = async (orderId, newStatus, deliveredBy = null) => {
    try {
      await orderService.updateOrderStatus(orderId, newStatus, deliveredBy);
      
      // State'i güncelle ve cache'e kaydet
      setActiveOrders(prev => {
        const updated = prev.map(order => {
          if (order._id === orderId || order.id === orderId) {
            return { ...order, status: newStatus };
          }
          return order;
        });
        
        // Eğer delivered veya cancelled ise aktif listeden çıkar, tamamlanan listeye ekle
        if (newStatus === 'delivered' || newStatus === 'cancelled') {
          const movedOrder = updated.find(o => (o._id === orderId || o.id === orderId));
          if (movedOrder) {
            const updatedOrder = { ...movedOrder, status: newStatus };
            setCompletedOrders(prevCompleted => {
              const newCompleted = [updatedOrder, ...prevCompleted];
              // Cache'e kaydet
              offlineStorage.saveCompletedOrders(newCompleted).catch(err => 
                console.error('Cache kaydetme hatası:', err)
              );
              return newCompleted;
            });
            
            // Cache'e tek sipariş olarak da kaydet
            offlineStorage.saveOrder(updatedOrder).catch(err => 
              console.error('Cache kaydetme hatası:', err)
            );
            
            const filtered = updated.filter(o => o._id !== orderId && o.id !== orderId);
            // Cache'e güncel aktif siparişleri kaydet
            offlineStorage.saveActiveOrders(filtered).catch(err => 
              console.error('Cache kaydetme hatası:', err)
            );
            return filtered;
          }
        } else {
          // Durum güncellendi, cache'e kaydet
          const updatedOrder = updated.find(o => (o._id === orderId || o.id === orderId));
          if (updatedOrder) {
            offlineStorage.saveOrder(updatedOrder).catch(err => 
              console.error('Cache kaydetme hatası:', err)
            );
          }
          // Cache'e tüm aktif siparişleri kaydet
          offlineStorage.saveActiveOrders(updated).catch(err => 
            console.error('Cache kaydetme hatası:', err)
          );
        }
        
        return updated;
      });
      
      // Son güncelleme zamanını güncelle
      await offlineStorage.setLastUpdateTime();
      
      toast({
        title: "Başarılı",
        description: "Sipariş durumu güncellendi",
        status: "success",
        duration: 3000,
        isClosable: true
      });
      
      // API'den de güncel veriyi çek (background'da)
      fetchOrders(completedTimeRange).catch(err => console.error('Background fetch hatası:', err));
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast({
        title: "Hata",
        description: "Sipariş durumu güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Online/Offline durumu göstergesi */}
      {!isOnline && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon as={MdWifiOff} />
          <AlertDescription>
            <HStack spacing={2}>
              <Text>Çevrimdışı moddasınız. Siparişler cache'den gösteriliyor.</Text>
              {lastUpdateTime && (
                <Badge colorScheme="orange">
                  Son güncelleme: {new Date(lastUpdateTime).toLocaleString('tr-TR')}
                </Badge>
              )}
            </HStack>
          </AlertDescription>
        </Alert>
      )}
      
      {isOnline && isFromCache && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon as={MdWifi} />
          <AlertDescription>
            Bağlantı kuruldu. Siparişler güncelleniyor...
          </AlertDescription>
        </Alert>
      )}

      <Tabs isFitted variant="enclosed" index={tabIndex} onChange={setTabIndex}>
        <TabList mb="1em">
          <Tab _selected={{ color: 'white', bg: '#e45858' }}>
            Aktif Siparişler {activeOrders.length > 0 && `(${activeOrders.length})`}
          </Tab>
          <Tab _selected={{ color: 'white', bg: '#e45858' }}>
            Tamamlanan Siparişler
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0}>
            <TableBasedOrdersView 
              orders={activeOrders}
              onStatusChange={handleStatusChange}
              isLoading={loading}
            />
          </TabPanel>
          
          <TabPanel p={0}>
            <DoneOrdersTable 
              completedOrders={completedOrders}
              isLoading={loading}
              onTimeRangeChange={async (timeRange) => {
                setCompletedTimeRange(timeRange);
                await fetchOrders(timeRange);
              }}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}